import {
  Button,
  ColorCodes,
  Divider,
  Fab,
  FormMessage,
  FormMessageVariant,
  IconIdentifier,
  ImageUpload,
  Input,
  InputGroup,
  Placement,
  SideCard,
  SideCardLocation,
  Spinner,
  Tooltip,
} from '@aus-platform/design-system';
import { isEmpty, isNil, isNull, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  handleResponseErrorMessage,
  handleResponseMessage,
  LayerType,
  useAltitude,
  useFeatureInfo,
  useUpdateFeatureInfo,
} from 'src/shared/api';
import {
  FeatureType,
  useDeleteImage,
  useFeatureProperties,
} from 'src/shared/api/features';
import { ConfirmationModal, ImagePreviewModal } from 'src/shared/components';
import { CoordinatesName } from 'src/shared/enums';
import {
  isFileSafe,
  fillArray,
  getAreaFormattedUnitValue,
  getDDValues,
  getDistanceFormattedUnitValue,
  getDMSValues,
  getUTMCoordinates,
  isProjectedCrs,
  metricMeasurementUnits,
  UnitSystem,
} from 'src/shared/helpers';
import { useInputFields } from 'src/shared/hooks';
import wkt from 'wkt';
import {
  destroyMeasureTool,
  selectMap3DState,
  selectMap3dWorkspace,
  setCurrentActiveMapTool,
  setRightSideCard,
  WorkspaceRightSideCard,
} from '../../../../shared/map-3d-slices';
import { FeatureInfoTool } from '../../../../shared/map-3d-tools';
import { ToolEvent } from '../../../../shared/map-3d-tools/enums';
import type { IFeatureInfoTool } from '../../../../shared/map-3d-tools/interfaces';
import { MapTool } from '../enums';
import {
  LineMeasurementDetails,
  PointCoordinatesDetails,
  PolygonMeasurementDetails,
} from './components';
import {
  coordinateFormatOptions,
  featureInfoInitialInputState,
  initialCoordinateFormats,
  initialFeatureProperties,
  initialImageState,
  lineMeasurementOptions,
  polygonMeasurementOptions,
} from './constant';
import {
  CoordinateFormatType,
  DefaultCrs,
  FeatureInfoType,
  LineMeasurementUnit,
  MeasurementType,
  PolygonMeasurementUnit,
  UnitSystemKey,
} from './enums';
import { MeasurementInfo } from './measurement-info';
import type {
  CoordinateFormats,
  FeatureImagePreviewerStateType,
  FeatureInfoInputType,
  FeaturePropertiesType,
  MeasurementOptionsType,
} from './types';

export const FeatureInfo: React.FC = () => {
  // Dispatcher.
  const dispatch = useAppDispatch();

  // Selector.
  const {
    cesiumProxy,
    currentActiveMapTool,
    isElevationProfileSelectLayersActive,
  } = useAppSelector(selectMap3DState);
  const { drawingTools, rightSideCard, workspaceLayers } =
    useAppSelector(selectMap3dWorkspace);

  // useStates.
  const [imageIdToBeDeleted, setImageIdToBeDeleted] = useState<string | null>();
  const [featureImagePreviewerModal, setFeatureImagePreviewerModal] =
    useState<FeatureImagePreviewerStateType>({
      show: false,
      selectedImageIndex: null,
      selectedFeatureImage: null,
    });
  const [attributes, setAttributes] = useState<Record<string, any>>();
  const [featureInfoTool, setFeatureInfoTool] = useState<IFeatureInfoTool>();
  const [altitude, setAltitude] = useState<number | null>(null);
  const [featureProperties, setFeatureProperties] =
    useState<FeaturePropertiesType>(initialFeatureProperties);
  const [featureMeasurementOptions, setFeatureMeasurementOptions] = useState<
    MeasurementOptionsType[]
  >([]);
  const [pointFeatureCoordinates, setPointFeatureCoordinates] =
    useState<CoordinateFormats>(initialCoordinateFormats);

  // Hooks.
  const {
    values,
    names,
    errors,
    setValues,
    onChange,
    onBlur,
    onFocus,
    inputHasError,
    inputIsDirty,
    resetAll,
    optional,
    setOptional,
    getOptionalFields,
  } = useInputFields<FeatureInfoInputType>(featureInfoInitialInputState);

  // APIs.
  const {
    data: featureInfoResponse,
    isSuccess: isSuccessFeatureInfoResponse,
    isLoading: isLoadingFeatureInfoResponse,
    isError: isErrorFeatureInfoResponse,
    error: featureInfoResponseError,
  } = useFeatureInfo(values.featureId, !isEmpty(values.featureId));

  const {
    mutate: sendUpdateFeatureInfoRequest,
    data: updateFeatureInfoResponse,
    isError: isErrorUpdateFeatureInfo,
    isSuccess: isSuccessUpdateFeatureInfo,
    error: updateFeatureInfoError,
    isPending: isLoadingUpdateFeatureInfo,
  } = useUpdateFeatureInfo();

  const {
    mutate: sendDeleteImageRequest,
    isError: isErrorDeleteImageRequest,
    isSuccess: isSuccessDeleteImageRequest,
    error: deleteImageRequestError,
    data: deleteImageRequestData,
    isPending: isLoadingDeleteImageRequest,
  } = useDeleteImage();

  // Constants.
  const layer = featureInfoResponse
    ? workspaceLayers[featureInfoResponse?.data?.layer]
    : undefined;
  const hasManageLayerPermission = layer?.canManageLayer ?? false;
  const iteration = layer?.iteration;
  const sourceCrs = layer?.sourceCrs ?? DefaultCrs.EPSGCode;
  const terrainIterationId = featureInfoResponse?.data?.iterationId;
  const isPointFeatureType =
    featureInfoResponse?.data?.type === FeatureType.Point;
  const isLineFeatureType =
    featureInfoResponse?.data?.type === FeatureType.LineString;
  const isPolygonFeatureType =
    featureInfoResponse?.data?.type === FeatureType.Polygon;
  const isCadFeatureType = layer?.type === LayerType.Cad;
  const dsmStatus = featureInfoResponse?.data?.dsmStatus;
  const dsmCogStatus = featureInfoResponse?.data?.dsmStatusCog;
  const terrainTileStatus = featureInfoResponse?.data?.terrainTileStatus;
  const shouldFetchAltitude =
    isPointFeatureType &&
    !isCadFeatureType &&
    !!featureInfoResponse?.data?.geometry;

  const unitsWithBottomBorder: string[] = [
    LineMeasurementUnit.Kilometers,
    PolygonMeasurementUnit.Hectares,
  ];
  const customSelectOptionStyles = {
    option: (provided, state) => ({
      ...provided,
      borderBottom: unitsWithBottomBorder.includes(state.data.value)
        ? `0.1rem solid ${ColorCodes.Neutral100}`
        : '',
    }),
  };

  const {
    data: altitudeResponse,
    isSuccess: isSuccessAltitudeResponse,
    isFetching: isFetchingAltitude,
  } = useAltitude(
    {
      iterations: (terrainIterationId && [terrainIterationId]) || [],
      longitude: shouldFetchAltitude
        ? wkt
            .parse(featureInfoResponse?.data?.geometry)
            .coordinates[0].toFixed(6)
        : null,
      latitude: shouldFetchAltitude
        ? wkt
            .parse(featureInfoResponse?.data?.geometry)
            .coordinates[1].toFixed(6)
        : null,
    },
    shouldFetchAltitude,
  );

  const {
    data: featurePropertiesInfo,
    isSuccess: isSuccessFeaturePropertiesInfo,
    isFetching: isLoadingFeaturePropertiesInfo,
  } = useFeatureProperties(
    values.featureId,
    !isEmpty(values.featureId) &&
      isSuccessFeatureInfoResponse &&
      !isPointFeatureType &&
      !isCadFeatureType,
  );

  // Event listeners.
  const onFeatureFocusListener = ([featureId]) => {
    // Disable the feature info tool for drawn features that don't belong to any layer.
    if (
      drawingTools?.featureExists(featureId) &&
      !drawingTools?.featureHasProperty(featureId, 'layerId')
    ) {
      return;
    }

    if (currentActiveMapTool === MapTool.FeatureTool) {
      if (featureId === values.featureId) {
        setValues({
          ...values,
          featureId,
        });
      } else {
        setValues({
          ...featureInfoInitialInputState,
          featureId,
        });
      }
    }
  };

  const onFeatureBlurListener = () => {
    setValues(featureInfoInitialInputState);
    resetAll();
    setAttributes(undefined);
  };

  // useEffects.
  useEffect(() => {
    if (isSuccessAltitudeResponse && terrainIterationId) {
      const altitude = altitudeResponse.data.altitudes[terrainIterationId];

      setAltitude(altitude);
    }
  }, [isSuccessAltitudeResponse, altitudeResponse]);

  useEffect(() => {
    if (
      isSuccessFeaturePropertiesInfo &&
      featurePropertiesInfo &&
      featureInfoResponse
    ) {
      if (isLineFeatureType) {
        const { planarDistance, surfaceDistance } =
          featurePropertiesInfo?.data?.properties ?? {};

        setFeatureProperties({
          ...initialFeatureProperties,
          planarDistance,
          surfaceDistance,
        });
      } else if (isPolygonFeatureType) {
        const { planarArea, surfaceArea, planarPerimeter, surfacePerimeter } =
          featurePropertiesInfo?.data?.properties ?? {};

        setFeatureProperties({
          ...initialFeatureProperties,
          planarArea,
          surfaceArea,
          planarPerimeter,
          surfacePerimeter,
        });
      }
    }
  }, [
    isSuccessFeaturePropertiesInfo,
    featurePropertiesInfo,
    featureInfoResponse,
  ]);

  useEffect(() => {
    if (currentActiveMapTool !== MapTool.FeatureTool) {
      featureInfoTool?.deactivate();
      setValues(featureInfoInitialInputState);
      dispatch(setRightSideCard(WorkspaceRightSideCard.None));
    } else if (currentActiveMapTool === MapTool.FeatureTool) {
      featureInfoTool?.activate();
    }
  }, [currentActiveMapTool]);

  useEffect(() => {
    setOptional({
      ...optional,
      featureInformation: true,
      featureImages: true,
      featureMeasurementFormat: true,
    });

    if (!isEmpty(values.featureId)) {
      dispatch(setRightSideCard(WorkspaceRightSideCard.FeatureInfo));
    } else {
      dispatch(setRightSideCard(WorkspaceRightSideCard.None));
    }
  }, [values.featureId]);

  useEffect(() => {
    if (!cesiumProxy?.viewer) {
      return;
    }

    const featureInfoTool = new FeatureInfoTool(cesiumProxy.cesiumViewer);

    setFeatureInfoTool(featureInfoTool);

    featureInfoTool.addEventListener(ToolEvent.Blur, onFeatureBlurListener);
    featureInfoTool.addEventListener(ToolEvent.Focus, onFeatureFocusListener);

    return () => {
      featureInfoTool.removeEventListener(
        ToolEvent.Blur,
        onFeatureBlurListener,
      );
      featureInfoTool.removeEventListener(
        ToolEvent.Focus,
        onFeatureFocusListener,
      );
    };
  }, [cesiumProxy?.viewer, drawingTools, currentActiveMapTool, values]);

  useEffect(() => {
    const selectedUnitSystem = localStorage.getItem(
      UnitSystemKey.Selected,
    ) as UnitSystem;

    if (featureInfoResponse && isSuccessFeatureInfoResponse) {
      const featureImageArray = featureInfoResponse.data.images?.map(
        (image) => {
          return {
            ...image,
            isUploaded: true,
            imageFile: null,
            imageSource: image.imageS3Key,
            thumbnailSource: image.thumbnailS3Key,
          };
        },
      );

      setAttributes(featureInfoResponse.data.attributes);

      const updatedValues = {
        ...values,
        // Text area's value should always be a string even if the value is empty.
        featureInformation: featureInfoResponse.data.info ?? '',
        ...(!isNil(featureImageArray) && {
          featureImages:
            featureImageArray.length < 3
              ? [
                  ...featureImageArray,
                  ...fillArray(3 - featureImageArray.length, initialImageState),
                ]
              : featureImageArray,
        }),
      };

      if (isPointFeatureType) {
        const isCrsProjected = isProjectedCrs(sourceCrs);
        const defaultFormat = isCrsProjected
          ? CoordinateFormatType.UTM
          : CoordinateFormatType.DecimalDegrees;
        const defaultFormatOption = coordinateFormatOptions.find(
          (option) => option.value === defaultFormat,
        )!;

        updatedValues.featureMeasurementFormat = defaultFormatOption;
        setFeatureMeasurementOptions(coordinateFormatOptions);
      } else if (isLineFeatureType) {
        const autoFormattedUnit = getDistanceFormattedUnitValue(
          featureProperties.planarDistance,
          selectedUnitSystem,
        );

        const defaultLineFormat = lineMeasurementOptions.find(
          (option) => option.value === autoFormattedUnit,
        )!;

        updatedValues.featureMeasurementFormat = defaultLineFormat;
        setFeatureMeasurementOptions(lineMeasurementOptions);
      } else {
        const autoFormattedUnit = getAreaFormattedUnitValue(
          featureProperties.planarArea,
          selectedUnitSystem,
        );

        const defaultPolygonFormat = polygonMeasurementOptions.find(
          (option) => option.value === autoFormattedUnit,
        )!;

        updatedValues.featureMeasurementFormat = defaultPolygonFormat;
        setFeatureMeasurementOptions(polygonMeasurementOptions);
      }

      setValues(updatedValues);
    }
    handleResponseErrorMessage(
      isErrorFeatureInfoResponse,
      featureInfoResponseError,
    );
  }, [
    isSuccessFeatureInfoResponse,
    featureInfoResponse,
    isErrorFeatureInfoResponse,
    featureInfoResponseError,
    featureProperties,
  ]);

  useEffect(() => {
    if (isSuccessUpdateFeatureInfo && updateFeatureInfoResponse) {
      const featureImageArray = updateFeatureInfoResponse.data.images?.map(
        (image) => {
          return {
            ...image,
            imageSource: image.imageS3Key,
            thumbnailSource: image.thumbnailS3Key,
            isUploaded: true,
            imageFile: null,
          };
        },
      );

      setValues({
        ...values,
        featureInformation: updateFeatureInfoResponse.data.info ?? '',
        // Fill array with init state, when the images in be is less than 3.
        ...(!isNil(featureImageArray) && {
          featureImages:
            featureImageArray.length < 3
              ? [
                  ...featureImageArray,
                  ...fillArray(3 - featureImageArray.length, initialImageState),
                ]
              : featureImageArray,
        }),
      });
    }
    handleResponseMessage(
      isSuccessUpdateFeatureInfo,
      isErrorUpdateFeatureInfo,
      updateFeatureInfoResponse,
      updateFeatureInfoError,
    );
  }, [isSuccessUpdateFeatureInfo, isErrorUpdateFeatureInfo]);

  useEffect(() => {
    if (isSuccessDeleteImageRequest) {
      setValues((prevValue) => {
        const images = prevValue.featureImages.map((featureImage) => {
          if (featureImage.id === imageIdToBeDeleted) {
            return { ...initialImageState };
          }
          return featureImage;
        });

        return {
          ...prevValue,
          featureImages: images,
        };
      });

      setImageIdToBeDeleted(null);
    }
    handleResponseMessage(
      isSuccessDeleteImageRequest,
      isErrorDeleteImageRequest,
      deleteImageRequestData,
      deleteImageRequestError,
    );
  }, [isSuccessDeleteImageRequest, isErrorDeleteImageRequest]);

  useEffect(() => {
    if (!featureInfoResponse?.data?.geometry) {
      return;
    }

    if (isPointFeatureType) {
      const { coordinates } = wkt.parse(featureInfoResponse?.data.geometry);
      const [longitude, latitude]: [number, number] = coordinates;

      switch (values.featureMeasurementFormat?.value) {
        case CoordinateFormatType.UTM: {
          const { zoneNumber, utmCoords, hemisphere } = getUTMCoordinates(
            longitude,
            latitude,
            sourceCrs,
          );
          setPointFeatureCoordinates({
            ...pointFeatureCoordinates,
            zoneNumber,
            utmCoords,
            hemisphere,
          });
          break;
        }
        case CoordinateFormatType.DecimalDegrees: {
          const [ddLongitude, ddLatitude] = getDDValues(longitude, latitude);
          setPointFeatureCoordinates({
            ...pointFeatureCoordinates,
            ddLatitude,
            ddLongitude,
          });
          break;
        }
        case CoordinateFormatType.DegreesMinutesSeconds: {
          const dmsLongitude = getDMSValues(longitude);
          const dmsLatitude = getDMSValues(latitude, true);
          setPointFeatureCoordinates({
            ...pointFeatureCoordinates,
            dmsLatitude,
            dmsLongitude,
          });
          break;
        }
      }
    }
  }, [featureInfoResponse?.data?.geometry, values.featureMeasurementFormat]);

  // Handlers.
  const displayFeatureImagePreviewModal = (imageIndex: number) => {
    setFeatureImagePreviewerModal({
      show: true,
      selectedImageIndex: imageIndex,
      selectedFeatureImage: values.featureImages?.[imageIndex],
    });
  };

  const hideFeatureImagePreviewModal = () => {
    setFeatureImagePreviewerModal((prev) => ({
      ...prev,
      show: false,
    }));
  };

  const onImagePreviewerLoadSuccessHandler = () => {
    setFeatureImagePreviewerModal((prev) => ({ ...prev, isLoading: false }));
  };

  const onClickNextImagePreviewModalHandler = () => {
    const { selectedImageIndex } = featureImagePreviewerModal;

    // If we are already at last image, do nothing.
    if (isNull(selectedImageIndex) || selectedImageIndex > 1) {
      return;
    }

    let updatedFeatureImageIndex = selectedImageIndex;
    const nextPopulatedFeatureImageObject = values.featureImages
      .slice(selectedImageIndex + 1)
      .find((featureImageObject) => {
        updatedFeatureImageIndex++;

        return !isNull(featureImageObject.imageSource);
      });

    if (!isUndefined(nextPopulatedFeatureImageObject)) {
      setFeatureImagePreviewerModal({
        show: true,
        selectedImageIndex: updatedFeatureImageIndex,
        selectedFeatureImage: nextPopulatedFeatureImageObject,
      });
    }
  };

  const onClickPreviousImagePreviewModalHandler = () => {
    const { selectedImageIndex } = featureImagePreviewerModal;

    // If we are already at first image, do nothing.
    if (isNull(selectedImageIndex) || selectedImageIndex < 1) {
      return;
    }

    let updatedFeatureImageIndex = selectedImageIndex;
    const previousPopulatedFeatureImageObject = values.featureImages
      .slice(0, selectedImageIndex)
      .reverse()
      .find((featureImageObject) => {
        updatedFeatureImageIndex--;

        return (
          !isNull(featureImageObject.imageSource) ||
          !isNull(featureImageObject.imageFile)
        );
      });

    if (!isUndefined(previousPopulatedFeatureImageObject)) {
      setFeatureImagePreviewerModal({
        show: true,
        selectedImageIndex: updatedFeatureImageIndex,
        selectedFeatureImage: previousPopulatedFeatureImageObject,
      });
    }
  };

  const onFeatureInfoToolClick = () => {
    if (!featureInfoTool) {
      return;
    }

    if (currentActiveMapTool === MapTool.MeasureTool) {
      destroyMeasureTool();
      dispatch(setCurrentActiveMapTool(MapTool.FeatureTool));
    }

    if (currentActiveMapTool === MapTool.FeatureTool) {
      dispatch(setCurrentActiveMapTool(MapTool.None));
    } else {
      dispatch(setCurrentActiveMapTool(MapTool.FeatureTool));
    }
  };

  const onCloseFeatureInfoSideCard = () => {
    setValues(featureInfoInitialInputState);
    setAttributes(undefined);
  };

  const onSubmit = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    if (!inputHasError(getOptionalFields()) && inputIsDirty()) {
      sendUpdateFeatureInfoRequest({
        id: values.featureId,
        info: values.featureInformation,
        // To not include null values.
        images: values.featureImages?.reduce(
          (prevFeatureImagesList: File[], currentFeatureImage) => {
            if (!isNull(currentFeatureImage.imageFile)) {
              prevFeatureImagesList.push(currentFeatureImage.imageFile);
            }
            return prevFeatureImagesList;
          },
          [],
        ),
      });
    }
  };

  const onFeatureImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    imageIndex: number,
  ) => {
    const file = event.target.files?.[0];

    if (!isNil(file)) {
      const isAllowed = await isFileSafe(file);

      if (!isAllowed) {
        event.target.value = '';
        return;
      }

      onChange(event);

      // Add image to particular index even if an image is already uploaded in that field.
      const featureImageObject = values.featureImages;

      featureImageObject[imageIndex].imageFile = file;
      featureImageObject[imageIndex].name = file.name;
      featureImageObject[imageIndex].isUploaded = false;

      // @TODO: Handle converting image into base64 in a hook to avoid repetition in multiple components.
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        const imageBase64String = String(reader.result);
        // Setting the image URL when file is selected.
        featureImageObject[imageIndex].imageSource = imageBase64String;
        featureImageObject[imageIndex].thumbnailSource = imageBase64String;

        setValues({
          ...values,
          featureImages: featureImageObject,
        });
      };
    }
  };

  const onFeatureImageDelete = (imageIndex: number) => {
    const image = values.featureImages[imageIndex];
    /**
     * Handle soft deletion of images.
     * In case user deletes an image which hasn't been uploaded yet.
     * On clicking update, the form would update with the soft-deleted images. Thus, preventing that.
     */
    if (!image.isUploaded) {
      setValues((prevValue) => {
        const images = prevValue.featureImages;
        images[imageIndex] = { ...initialImageState };
        return {
          ...prevValue,
          featureImages: images,
        };
      });
    }

    if (image.isUploaded) {
      setImageIdToBeDeleted(image.id);
    }
  };

  const handleMeasurementFormatChange = (
    selectedFormat: MeasurementOptionsType,
  ) => {
    setValues((prevValues) => ({
      ...prevValues,
      featureMeasurementFormat: selectedFormat,
    }));

    if (!isPointFeatureType) {
      if (metricMeasurementUnits.includes(selectedFormat.value)) {
        localStorage.setItem(UnitSystemKey.Selected, UnitSystem.Metric);
      } else {
        localStorage.setItem(UnitSystemKey.Selected, UnitSystem.Imperial);
      }
    }
  };

  // Renders.
  const formatOptionLabel = (option: MeasurementOptionsType) => {
    return (
      <div>
        <span>{option.label}</span>
        <span style={{ fontStyle: 'italic', color: ColorCodes.Neutral250 }}>
          {` (${option.customAbbreviation})`}
        </span>
      </div>
    );
  };

  const renderMeasurementDetails = () => {
    if (isPointFeatureType) {
      return (
        <>
          <PointCoordinatesDetails
            format={values.featureMeasurementFormat?.value}
            pointFeatureCoordinates={pointFeatureCoordinates}
          />
          <MeasurementInfo
            label={CoordinatesName.Altitude}
            value={altitude ? parseFloat(altitude.toFixed(7)) : altitude}
            isLoading={isFetchingAltitude}
            terrainIterationId={terrainIterationId}
            dsmStatus={dsmStatus}
            dsmCogStatus={dsmCogStatus}
            terrainTileStatus={terrainTileStatus}
          />
        </>
      );
    } else if (isLineFeatureType) {
      return (
        <LineMeasurementDetails
          featureProperties={featureProperties}
          measurementUnit={values.featureMeasurementFormat?.customAbbreviation}
          isLoading={isLoadingFeaturePropertiesInfo}
          terrainIterationId={terrainIterationId}
          dsmStatus={dsmStatus}
          dsmCogStatus={dsmCogStatus}
          terrainTileStatus={terrainTileStatus}
        />
      );
    }

    return (
      <PolygonMeasurementDetails
        featureProperties={featureProperties}
        measurementUnit={values.featureMeasurementFormat?.customAbbreviation}
        isLoading={isLoadingFeaturePropertiesInfo}
        terrainIterationId={terrainIterationId}
        dsmStatus={dsmStatus}
        dsmCogStatus={dsmCogStatus}
        terrainTileStatus={terrainTileStatus}
      />
    );
  };

  return (
    <>
      <div className="map-3d-tools map-3d-tools--feature-info">
        <Tooltip hoverText="Inspect" placement={Placement.Right}>
          <Fab
            leftIconIdentifier={IconIdentifier.InfoCircle}
            onClick={onFeatureInfoToolClick}
            active={currentActiveMapTool === MapTool.FeatureTool}
            disabled={isElevationProfileSelectLayersActive}
          />
        </Tooltip>
      </div>

      <SideCard
        show={rightSideCard === WorkspaceRightSideCard.FeatureInfo}
        showCloseButton={true}
        title="Feature Info"
        placement={SideCardLocation.End}
        onClose={onCloseFeatureInfoSideCard}
        className="map-3d-feature-info-sidecard"
        backdrop={false}
        footer={
          <Button
            className="ms-auto"
            disabled={inputHasError(getOptionalFields()) || !inputIsDirty()}
            onClick={onSubmit}
            isLoading={isLoadingUpdateFeatureInfo}
          >
            Update
          </Button>
        }
      >
        {isLoadingFeatureInfoResponse ? (
          <Spinner />
        ) : (
          <div className="map-3d-feature-info-sidecard__form">
            <div className="map-3d-feature-info-sidecard__feature-type">
              <Divider text="Feature Type" />
              {isLineFeatureType
                ? FeatureInfoType.Line
                : featureInfoResponse?.data?.type}
            </div>
            {!isCadFeatureType && (
              <>
                <div className="map-3d-feature-info-sidecard__feature-measurement">
                  <Divider
                    text={isPointFeatureType ? 'Coordinates' : 'Measurements'}
                  />
                  <InputGroup>
                    <Input.Label>
                      {isPointFeatureType
                        ? MeasurementType.Format
                        : MeasurementType.Units}
                    </Input.Label>
                    <Input.Select
                      options={featureMeasurementOptions}
                      formatOptionLabel={formatOptionLabel}
                      styles={customSelectOptionStyles}
                      value={values.featureMeasurementFormat}
                      name={names.featureMeasurementFormat}
                      onChange={handleMeasurementFormatChange}
                      {...{ onBlur, onFocus }}
                    />
                  </InputGroup>
                </div>
                <div className="map-3d-feature-info-sidecard__measurement-details">
                  {renderMeasurementDetails()}
                </div>

                {terrainIterationId && (
                  <FormMessage
                    className="map-3d-feature-info-sidecard__dem-info"
                    variant={FormMessageVariant.Info}
                    message={
                      <div>
                        Using DEM of <strong>{iteration?.name}</strong> for{' '}
                        {isPointFeatureType ? 'altitude' : 'measurements'}
                      </div>
                    }
                  />
                )}
              </>
            )}
            {/* Attribute table */}
            <InputGroup className="w-100">
              <Divider text="Attributes" />
              <div className="map-3d-feature-info-sidecard__attribute-table__container">
                {attributes ? (
                  <Table
                    bordered
                    hover
                    responsive
                    size="sm"
                    className="map-3d-feature-info-sidecard__attribute-table"
                  >
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(attributes).map(([key, value], index) => (
                        <tr key={index}>
                          <td>{key}</td>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <span className="empty">No attribute is present.</span>
                )}
              </div>
            </InputGroup>

            <InputGroup className="map-3d-feature-info-sidecard__feature-information">
              <Divider text="Feature Information" />
              <Input.TextArea
                disabled={!hasManageLayerPermission}
                placeholder="Feature Information"
                value={values.featureInformation}
                name={names.featureInformation}
                error={errors.featureInformation}
                isInvalid={!!errors.featureInformation}
                {...{ onChange, onBlur, onFocus }}
              />
            </InputGroup>

            <InputGroup>
              <Divider text="Feature Images" />
              <div className="map-3d-feature-info-sidecard__image-container">
                {!isNil(imageIdToBeDeleted) && (
                  <ConfirmationModal
                    message="Are you sure you want to delete this image?"
                    onSubmit={() => {
                      sendDeleteImageRequest(imageIdToBeDeleted);
                    }}
                    onClose={() => {
                      setImageIdToBeDeleted(null);
                    }}
                  />
                )}
                {Array(3)
                  .fill(null)
                  .map((_, index) => (
                    <ImageUpload
                      disabled={!hasManageLayerPermission}
                      key={index}
                      name={names.featureImages}
                      onChange={(event) => {
                        onFeatureImageChange(event, index);
                      }}
                      onDelete={() => onFeatureImageDelete(index)}
                      isLoading={
                        isLoadingUpdateFeatureInfo ||
                        isLoadingDeleteImageRequest ||
                        isLoadingFeatureInfoResponse
                      }
                      imageSrc={values.featureImages[index]?.thumbnailSource}
                      onClickView={() => displayFeatureImagePreviewModal(index)}
                    />
                  ))}
              </div>
            </InputGroup>
            <InputGroup>
              <Divider text="Feature ID" />
              <Input.Text
                value={values.featureId}
                name={names.featureId}
                error={errors.featureId}
                isInvalid={!!errors.featureId}
                disabled
                {...{ onChange, onBlur, onFocus }}
              />
            </InputGroup>
          </div>
        )}

        <ImagePreviewModal
          show={featureImagePreviewerModal.show}
          onClose={hideFeatureImagePreviewModal}
          source={
            featureImagePreviewerModal.selectedFeatureImage?.imageSource || ''
          }
          title={featureImagePreviewerModal.selectedFeatureImage?.name || ''}
          onClickNextImage={onClickNextImagePreviewModalHandler}
          onClickPreviousImage={onClickPreviousImagePreviewModalHandler}
          onImageLoadSuccess={onImagePreviewerLoadSuccessHandler}
        />
      </SideCard>
    </>
  );
};
